import React, { useRef, useState } from "react";
import {
  Card, CardContent, Box, Typography, Chip, Fade, alpha,
  IconButton, Menu, MenuItem, ListItemIcon, ListItemText,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import ArchiveIcon from "@mui/icons-material/Archive";
import { KnowledgehubFileResponse } from "../../../../app/models/responseModels/knowledgehubResponse";
import { getFileConfig, formatDate } from "../constants/knowledgehubConstants";
import { useSelector } from "react-redux";
import { RootState } from "../../../../app/features/store/store";
import { PAGES } from "../../../../enums/PagesEnum";
import { toast } from "react-toastify";

type Props = {
  file: KnowledgehubFileResponse;
  index: number;
  folderCount: number;
  isHovered: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
  onDelete: (file: KnowledgehubFileResponse) => void;
  onReupload: (file: KnowledgehubFileResponse, newFile: File) => void;
  onDownload: (file: KnowledgehubFileResponse) => void;
  onEdit: (file: KnowledgehubFileResponse) => void;
  onArchive: (file: KnowledgehubFileResponse) => void;
};

export default function KnowledgehubFileCard({
  file, index, folderCount, isHovered,
  onMouseEnter, onMouseLeave, onClick, onDelete, onReupload, onDownload, onEdit, onArchive,
}: Props) {
  const config = getFileConfig(file.fileType);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMenuActionRef = useRef(false);

  const ALLOWED_FILE_TYPES = [".pdf", ".doc", ".docx", ".ppt", ".pptx", ".mp4"];

  const validateFile = (file: File): boolean => {
    const ext = "." + file.name.split(".").pop()?.toLowerCase();
    if (!ALLOWED_FILE_TYPES.includes(ext)) {
      toast.error(`Invalid file type: ${file.name}. Only PDF, WORD, PPT, and MP4 files are allowed.`);
      return false;
    }
    return true;
  };

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAnchorEl(null);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAnchorEl(null);
    onDelete(file);
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAnchorEl(null);
    onDownload(file);
  };

  const handleReuploadClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAnchorEl(null);
    isMenuActionRef.current = true;
    setTimeout(() => fileInputRef.current?.click(), 100);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAnchorEl(null);
    onEdit(file);
  };

  const handleArchive = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAnchorEl(null);
    onArchive(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    isMenuActionRef.current = false;
    const selected = e.target.files?.[0];
    if (selected && validateFile(selected)) {
      onReupload(file, selected);
    }
    e.target.value = "";
  };

  // ***** Get page Permissions *****
  const pagePermission = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings?.pagePermissions.find(
      (x) => x.pageName === PAGES.KNOWLEDGE_HUB
    )
  );

  const IsCreatePermission = pagePermission
    ? pagePermission.isCreatePermission
    : false;

  const IsAdmin = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings
      ? state.login?.authResponse?.userSettings?.roles.includes(
        "Administrators"
      )
      : false
  );

  return (
    <Fade in timeout={300 + (folderCount + index) * 50}>
      <Card
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={() => {
          if (isMenuActionRef.current) return;
          onClick();
        }}
        sx={{
          cursor: "pointer",
          borderRadius: 3,
          transition: "all 0.2s ease",
          transform: isHovered ? "translateY(-4px)" : "none",
          boxShadow: isHovered
            ? `0px 8px 24px ${alpha(config.color, 0.15)}`
            : "0px 2px 8px rgba(0, 0, 0, 0.05)",
          border: "1px solid rgba(0, 0, 0, 0.08)",
          "&:hover": { borderColor: alpha(config.color, 0.3) },
        }}
      >
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
            <Box sx={{ mr: 1.5, color: config.color, display: "flex" }}>
              {config.icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={500} noWrap>
                {file.title}
              </Typography>
              {file.description && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {file.description}
                </Typography>
              )}
            </Box>

            {/* Three-dot menu */}
            {IsCreatePermission && IsAdmin && ( <IconButton
              size="small"
              onClick={handleMenuOpen}
              sx={{ ml: 0.5, color: "text.secondary" }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>)}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
            <Chip
              label={file.fileType.toUpperCase()}
              size="small"
              sx={{
                bgcolor: alpha(config.color, 0.1),
                color: config.color,
                fontWeight: 500,
                fontSize: "0.7rem",
              }}
            />
            {file.updated_on && (
              <Typography variant="caption" color="text.disabled">
                Updated {formatDate(file.updated_on)}
              </Typography>
            )}
          </Box>
        </CardContent>

        {/* Hidden file input for reupload */}
        <input
          ref={fileInputRef}
          type="file"
          hidden
          accept=".pdf,.doc,.docx,.ppt,.pptx,.mp4"
          onChange={handleFileChange}
        />

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
          PaperProps={{ sx: { borderRadius: 2, minWidth: 160, boxShadow: "0px 4px 16px rgba(0,0,0,0.12)" } }}
        >
          <MenuItem onClick={handleEdit}>
            <ListItemIcon><EditIcon fontSize="small" color="primary" /></ListItemIcon>
            <ListItemText>Access Management</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDownload}>
            <ListItemIcon><DownloadIcon fontSize="small" color="primary" /></ListItemIcon>
            <ListItemText>Download</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleReuploadClick}>
            <ListItemIcon><UploadFileIcon fontSize="small" color="primary" /></ListItemIcon>
            <ListItemText>Replace</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleArchive}>
            <ListItemIcon><ArchiveIcon fontSize="small" color="warning" /></ListItemIcon>
            <ListItemText>Version history</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
            <ListItemIcon><DeleteOutlineIcon fontSize="small" sx={{ color: "error.main" }} /></ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Menu>
      </Card>
    </Fade>
  );
}
